import { merge } from "lodash-es";

import {
  ChatDto,
  ChatParticipantDto,
  ChatParticipantStatus,
  ChatType,
  PartyType,
} from "@/core/api/generated/v1";

export interface ChatPermissions {
  canViewChat: boolean;
  canJoinChat: boolean;
  canLeaveChat: boolean;
  canViewChatActions: boolean;
  canUpdateChat: boolean;
  canUpdateChatTags: boolean;
  canResolveChat: boolean;
  canReopenChat: boolean;
  canAcknowledgeChat: boolean;
  canViewParticipants: boolean;
  canManageParticipants: boolean;
  canViewUserSettings: boolean;
  canUpdateUserSettings: boolean;
  canViewEventHistory: boolean;
  canViewActivities: boolean;
  canViewNegotiations: boolean;
  canPinItems: boolean;
  canSendMessage: boolean;
  canUpdateMessage: boolean;
  canUpdateMessageTags: boolean;
  canDeleteMessage: boolean;
  canViewMessageActions: boolean;
  canAcknowledgeMessage: boolean;
  shouldMarkMessagesAsRead: boolean;
  shouldCloseChatIfParticipantLeft: boolean;
}

/** Context passed from the parent comps where chat is rendered to provide additional context. */
export interface ChatExtraContext {
  tenantToTenant?: ChatTenantToTenantExtraContext;
}

export interface ChatTenantToTenantExtraContext {
  tenant1Id: string;
  tenant2Id: string;
}

export interface ChatTenantToTenantContext {
  /** Current tenant accessing the chat. */
  currentTenantId: string;
  /** 2nd tenant from the T2T connection in the chat.  */
  connectedTenantId?: string;
  /** Whether chat is between 2 tenants. */
  isTenantToTenant: boolean;
  /** Whether current tenant can view and mention approved contact users from the 2nd tenant. */
  isAllowExternalUserMentions: boolean;
}

export class ChatHelper {
  public static getPermissions(params: {
    chat: Nil<ChatDto>;
    participants: Nil<ChatParticipantDto[]>;
    currentParticipant: Nil<ChatParticipantDto>;
    /** NB: undefined keys are skipped, null not! */
    defaultValues?: Partial<ChatPermissions>;
  }): ChatPermissions {
    const isActiveParticipant =
      !!params.currentParticipant &&
      params.currentParticipant.status === ChatParticipantStatus.Active;
    const canViewChat =
      !!params.chat && (!params.chat.isRestrictedParticipants || isActiveParticipant); // BE handles most of the view permissions

    const computed: ChatPermissions = {
      canViewChat,
      canJoinChat:
        canViewChat &&
        !isActiveParticipant &&
        (!!params.chat?.settings?.allowJoin || !!params.chat?.settings?.allowPartyMemberJoin) &&
        (!params.currentParticipant ||
          params.currentParticipant.status === ChatParticipantStatus.Left),
      canLeaveChat: !!params.chat?.settings?.allowLeave && isActiveParticipant,
      canViewChatActions: isActiveParticipant,
      canUpdateChat: isActiveParticipant && !!params.chat?.settings!.allowEdit,
      canUpdateChatTags: isActiveParticipant,
      canResolveChat: isActiveParticipant,
      canReopenChat: isActiveParticipant,
      canAcknowledgeChat: isActiveParticipant,
      canViewParticipants: isActiveParticipant,
      canManageParticipants:
        isActiveParticipant && !!params.chat?.settings?.allowParticipantManagement,
      canViewUserSettings: isActiveParticipant,
      canUpdateUserSettings: isActiveParticipant,
      canViewEventHistory: canViewChat,
      canViewActivities: canViewChat,
      canViewNegotiations: canViewChat && params.chat?.type === ChatType.Negotiation,
      canPinItems: isActiveParticipant,
      canSendMessage: isActiveParticipant,
      canUpdateMessage: isActiveParticipant,
      canUpdateMessageTags: isActiveParticipant,
      canDeleteMessage: isActiveParticipant,
      canViewMessageActions: isActiveParticipant,
      canAcknowledgeMessage: isActiveParticipant,
      shouldMarkMessagesAsRead: isActiveParticipant,
      shouldCloseChatIfParticipantLeft: !!params.chat?.isRestrictedParticipants, // keep chat open if user can freely rejoin
    };
    return merge({}, computed, params.defaultValues || {});
  }

  public static getTenantToTenantContext(params: {
    currentTenantId: string;
    chat: ChatDto;
    extra: Nil<ChatTenantToTenantExtraContext>;
  }): ChatTenantToTenantContext {
    const isTenantToTenant = params.chat.isTenantToTenant ?? false;
    const connectedTenantId =
      params.chat.settings?.limitedToParties?.find(
        (x) => x.party?.type === PartyType.Tenant && x.party?.tenantId !== params.currentTenantId,
      )?.party?.tenantId || undefined;
    const tenantIds =
      isTenantToTenant && connectedTenantId
        ? [params.currentTenantId, connectedTenantId]
        : [params.currentTenantId];

    return {
      currentTenantId: params.currentTenantId,
      connectedTenantId: isTenantToTenant ? connectedTenantId : undefined,
      isTenantToTenant: isTenantToTenant,
      isAllowExternalUserMentions:
        isTenantToTenant &&
        !!connectedTenantId &&
        (!params.extra ||
          (tenantIds.includes(params.extra.tenant1Id) &&
            tenantIds.includes(params.extra.tenant2Id))),
    };
  }
}
